import { useState } from 'react';
import { cn } from '@/utils/cn';

interface TokenIconProps {
  symbol?: string | null;
  imageUrl?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const PALETTE = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-fuchsia-500',
  'bg-lime-500',
  'bg-orange-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-pink-500',
];

const sizeClasses = {
  xs: 'h-4 w-4 text-[8px]',
  sm: 'h-5 w-5 text-[9px]',
  md: 'h-6 w-6 text-[10px]',
  lg: 'h-8 w-8 text-[12px]',
} as const;

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function TokenIcon({ symbol, imageUrl, size = 'sm', className }: TokenIconProps) {
  const [imgError, setImgError] = useState(false);
  const normalizedSymbol = symbol?.trim() || null;
  const displayLabel = normalizedSymbol || 'Unknown token';
  const letter = (normalizedSymbol?.[0] ?? '?').toUpperCase();
  const paletteKey = normalizedSymbol || imageUrl || 'unknown-token';

  const colorClass = PALETTE[hashString(paletteKey.toUpperCase()) % PALETTE.length];
  const sizeClass = sizeClasses[size];

  if (imageUrl && !imgError) {
    return (
      <img
        src={imageUrl}
        alt={displayLabel}
        className={cn('shrink-0 rounded-full object-cover', sizeClass, className)}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white',
        sizeClass,
        colorClass,
        className,
      )}
      title={displayLabel}
    >
      {letter}
    </span>
  );
}
